import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthorization, ROLES } from '../../hooks/useAuthorization';
import { theme } from '../../styles/theme';

interface SidebarProps {
  isOpen: boolean;
  closeSidebar: () => void;
}

const SidebarContainer = styled.aside<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  background-color: ${theme.colors.background};
  box-shadow: ${theme.shadows.medium};
  z-index: 1000;
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.light};
`;

const SidebarTitle = styled.h2`
  font-size: ${theme.fontSizes.large};
  margin: 0;
`;

const UserInfoSection = styled.div`
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.light};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const UserAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${theme.colors.light};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    width: 36px;
    height: 36px;
    color: ${theme.colors.primary};
  }
`;

const UserName = styled.h3`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
`;

const UserEmail = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.gray};
  word-break: break-word;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.dark};
  font-size: ${theme.fontSizes.large};
  
  &:hover {
    color: ${theme.colors.danger};
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.md};
  color: ${theme.colors.dark};
  text-decoration: none;
  transition: background-color 0.2s;
  
  svg {
    margin-right: ${theme.spacing.sm};
  }
  
  &:hover {
    background-color: ${theme.colors.light};
    text-decoration: none;
  }
  
  &.active {
    color: ${theme.colors.primary};
    background-color: rgba(74, 144, 226, 0.1);
    font-weight: 600;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${theme.colors.light};
  margin: ${theme.spacing.md} 0;
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const Sidebar: React.FC<SidebarProps> = ({ isOpen, closeSidebar }) => {
  const { user, logout } = useAuth();
  const { hasRole } = useAuthorization();
  
  // Role checking
  const hasAppEditorRole = hasRole(ROLES.APP_EDITOR);
  const hasAdminRole = hasRole(ROLES.ADMIN);
  const isAppAdmin = hasAppEditorRole || hasAdminRole;
  
  const handleLogout = async () => {
    await logout();
    closeSidebar();
  };
  
  return (
    <>
      <Overlay isOpen={isOpen} onClick={closeSidebar} />
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader>
          <SidebarTitle>Menu</SidebarTitle>
          <CloseButton onClick={closeSidebar} aria-label="Close sidebar">
            ✕
          </CloseButton>
        </SidebarHeader>
        
        <UserInfoSection>
          <UserAvatar>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </UserAvatar>
          <UserName>
            {user?.name || 'User'}
          </UserName>
          <UserEmail>
            {user?.email || 'No email available'}
          </UserEmail>
        </UserInfoSection>
        
        <NavList>
          <NavItem>
            <StyledNavLink to="/" onClick={closeSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              Home
            </StyledNavLink>
          </NavItem>
          
          <NavItem>
            <StyledNavLink to="/courses" onClick={closeSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Courses
            </StyledNavLink>
          </NavItem>
          
          <NavItem>
            <StyledNavLink to="/community" onClick={closeSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Community
            </StyledNavLink>
          </NavItem>
          
          <NavItem>
            <StyledNavLink to="/profile" onClick={closeSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Profile
            </StyledNavLink>
          </NavItem>
          
          {isAppAdmin && (
            <>
              <Divider />
              <NavItem>
                <StyledNavLink to="/admin" onClick={closeSidebar}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  Admin Dashboard
                </StyledNavLink>
              </NavItem>
            </>
          )}
          
          <Divider />
          
          <NavItem>
            <StyledNavLink to="#" onClick={handleLogout}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              Logout
            </StyledNavLink>
          </NavItem>
        </NavList>
      </SidebarContainer>
    </>
  );
};

export default Sidebar; 